import { supabase } from '../../config/supabase';

/**
 * Get all users for a specific company (ungrouped)
 */
export const getUsers = async (companyId) => {
    try {
        const { data: users, error } = await supabase
            .from('users')
            .select('id, username, full_name, phone, role, position, email, avatar_url')
            .eq('status', 'active')
            .order('full_name', { ascending: true });

        if (error) throw error;

        return users || [];
    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
};

/**
 * Get all users grouped by role for a specific company
 */
export const getAllUsers = async (companyId) => {
    try {
        const { data: users, error } = await supabase
            .from('users')
            .select('id, username, full_name, phone, role, position, email')
            .eq('company_id', companyId)
            .eq('status', 'active')
            .order('username', { ascending: true });

        if (error) throw error;

        // Group users by role
        const grouped = {
            admins: users.filter(u => u.role === 'admin' || u.role === "super_admin"),
            designers: users.filter(u => u.role === 'designer'),
            estimators: users.filter(u => u.role === 'estimator'),
            drivers: users.filter(u => u.role === 'driver')
        };

        return grouped;
    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
};

/**
 * Get pending invitations for a company
 */
export const getPendingInvitations = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('user_invitations')
            .select('*')
            .eq('company_id', companyId)
            .eq('status', 'pending')
            .order('invited_at', { ascending: false });

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('Error fetching invitations:', error);
        throw error;
    }
};

/**
 * Invite a new user
 */
export const inviteUser = async (userData, companyId, companyName, invitedByUserId, invitedByName) => {
    try {
        // Validate required fields
        if (!userData.email || !userData.username || !userData.userType) {
            throw new Error('Email, username, and role are required');
        }

        // Validate phone if provided
        if (userData.phone) {
            const cleanPhone = userData.phone.replace(/\s/g, '');
            const phonePattern = /^[4][0-9]{8}$|^[2378][0-9]{8}$/;
            if (!phonePattern.test(cleanPhone)) {
                throw new Error('Invalid Australian phone number format');
            }
        }

        // Check if user already exists
        const { data: existingUser } = await supabase
            .from('users')
            .select('id')
            .eq('email', userData.email)
            .eq('company_id', companyId)
            .single();

        if (existingUser) {
            throw new Error('A user with this email already exists in your company');
        }

        // Check if there's already a pending invitation
        const { data: existingInvitation } = await supabase
            .from('user_invitations')
            .select('id')
            .eq('email', userData.email)
            .eq('company_id', companyId)
            .eq('status', 'pending')
            .single();

        if (existingInvitation) {
            throw new Error('An invitation has already been sent to this email');
        }

        // Create invitation
        // const { data: invitation, error: inviteError } = await supabase
        //     .from('user_invitations')
        //     .insert({
        //         company_id: companyId,
        //         email: userData.email,
        //         username: userData.username.trim(),
        //         role: userData.userType,
        //         position: userData.role || null,
        //         phone: userData.phone ? `+61${userData.phone.replace(/\D/g, '')}` : null,
        //         invited_by_user_id: invitedByUserId,
        //         invited_by_name: invitedByName,
        //         status: 'pending'
        //     })
        //     .select()
        //     .single();

        // if (inviteError) throw inviteError;

        // Generate invitation URL
        // const inviteUrl = `${window.location.origin}/accept-invitation/${invitation.id}`;

        // Send invitation email (you can customize this)
        // await sendInvitationEmail(userData.email, inviteUrl, userData.username);

        // Call Edge Function - it handles database insert AND email sending
        const { data, error } = await supabase.functions.invoke('invite-user', {
            body: {
                email: userData.email,
                username: userData.username.trim(),
                full_name: userData.full_name || null,
                role: userData.userType,
                position: userData.role || null,
                phone: userData.phone ? `+61${userData.phone.replace(/\D/g, '')}` : null,
                companyId: companyId,
                companyName: companyName,
                invitedByUserId: invitedByUserId,
                invitedByName: invitedByName
            }
        });

        if (error) throw error;

        return {
            message: `Invitation sent to ${userData.email}`,
            invitation: data.invitation,
            invitationUrl: data.invitationUrl
        };
    } catch (error) {
        console.error('Error inviting user:', error);
        throw error;
    }
};

/**
 * Cancel/delete an invitation
 */
export const cancelInvitation = async (invitationId) => {
    try {
        const { error } = await supabase
            .from('user_invitations')
            .update({ status: 'cancelled' })
            .eq('id', invitationId);

        if (error) throw error;

        return { message: 'Invitation cancelled successfully' };
    } catch (error) {
        console.error('Error cancelling invitation:', error);
        throw error;
    }
};

/**
 * Resend an invitation
 */
export const resendInvitation = async (invitationId) => {
    try {
        // Get invitation details
        const { data: invitation, error: fetchError } = await supabase
            .from('user_invitations')
            .select('*')
            .eq('id', invitationId)
            .single();

        if (fetchError) throw fetchError;

        // Update expiry date
        const { error: updateError } = await supabase
            .from('user_invitations')
            .update({
                expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                invited_at: new Date().toISOString()
            })
            .eq('id', invitationId);

        if (updateError) throw updateError;

        // Resend email
        const inviteUrl = `${window.location.origin}/accept-invitation/${invitation.id}`;
        await sendInvitationEmail(invitation.email, inviteUrl, invitation.username);

        return { message: 'Invitation resent successfully' };
    } catch (error) {
        console.error('Error resending invitation:', error);
        throw error;
    }
};

/**
 * Get invitation details (for accept page)
 */
export const getInvitationDetails = async (invitationId) => {

    try {
        const { data, error } = await supabase
            .from('user_invitations')
            .select(`
        *,
        companies (
          name,
          slug,
          logo_url
        )
      `)
            .eq('id', invitationId)
            .eq('status', 'pending')
            .single();

        if (error) throw error;

        // Check if expired
        if (new Date(data.expires_at) < new Date()) {
            throw new Error('This invitation has expired');
        }

        return data;
    } catch (error) {
        console.error('Error fetching invitation:', error);
        throw error;
    }
};

/**
 * Accept invitation and sign up
 */
export const acceptInvitation = async (invitationId, password) => {
    try {
        // Get invitation details
        const invitation = await getInvitationDetails(invitationId);

        // Sign up the user with Supabase Auth
        const { data: authData, error: signUpError } = await supabase.auth.signUp({
            email: invitation.email,
            password: password,
            options: {
                data: {
                    username: invitation.username,
                    role: invitation.role
                }
            }
        });

        if (signUpError) throw signUpError;

        // The database trigger will automatically create the user profile
        // and mark the invitation as accepted

        return {
            message: 'Account created successfully!',
            user: authData.user
        };
    } catch (error) {
        console.error('Error accepting invitation:', error);
        throw error;
    }
};

/**
 * Delete a user (soft delete - set status to inactive)
 */
export const deleteUser = async (userId) => {
    try {
        // Get user to check if they're super_admin
        const { data: userToDelete, error: fetchError } = await supabase
            .from('users')
            .select('role')
            .eq('id', userId)
            .single();

        if (fetchError) throw fetchError;

        // Prevent deletion of super_admin users
        if (userToDelete.role === 'super_admin') {
            throw new Error('Cannot delete super admin users');
        }

        // Soft delete - just set status to inactive
        const { error } = await supabase
            .from('users')
            .update({ status: 'inactive' })
            .eq('id', userId);

        if (error) throw error;

        return { message: 'User deleted successfully' };
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};

/**
 * Send invitation email
 * NOTE: This requires email configuration in Supabase or a custom email service
 */
const sendInvitationEmail = async (email, inviteUrl, username) => {
    try {
        // Option 1: Use Supabase's built-in email (requires email template setup)
        // For now, we'll just log the URL
        console.log(`Invitation URL for ${email}: ${inviteUrl}`);

        // Option 2: Use a custom email service (SendGrid, Mailgun, etc.)
        // const response = await fetch('your-email-service-endpoint', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({
        //     to: email,
        //     subject: 'You've been invited to join our team',
        //     html: `
        //       <h1>Welcome ${username}!</h1>
        //       <p>You've been invited to join our team.</p>
        //       <p>Click the link below to accept the invitation and create your account:</p>
        //       <a href="${inviteUrl}">Accept Invitation</a>
        //       <p>This link will expire in 7 days.</p>
        //     `
        //   })
        // });

        console.log(`
            <h1>Welcome ${username}!</h1>
              <p>You've been invited to join our team.</p>
           <p>Click the link below to accept the invitation and create your account:</p>
               <a href="${inviteUrl}">Accept Invitation</a>
               <p>This link will expire in 7 days.</p>
            `)

        // For development: Show alert with invite URL
        if (process.env.NODE_ENV === 'development') {
            alert(`DEVELOPMENT MODE: Invitation URL:\n\n${inviteUrl}\n\nCopy this URL to accept the invitation.`);
        }

        return true;
    } catch (error) {
        console.error('Error sending invitation email:', error);
        // Don't throw - invitation is created, email sending is secondary
        return false;
    }
};

/**
 * Validate Australian phone number
 */
export const validateAustralianPhone = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const mobilePattern = /^4[0-9]{8}$/;
    const landlinePattern = /^[2378][0-9]{8}$/;
    return mobilePattern.test(cleanPhone) || landlinePattern.test(cleanPhone);
};

/**
 * Format phone number for display (with spaces)
 */
export const formatPhoneDisplay = (phone) => {
    if (!phone) return '';

    // Remove +61 prefix if exists
    let digits = phone.replace(/^\+61/, '').replace(/\D/g, '');

    // Format: xxx xxx xxx
    if (digits.length >= 7) {
        return `${digits.substring(0, 3)} ${digits.substring(3, 6)} ${digits.substring(6)}`;
    } else if (digits.length >= 4) {
        return `${digits.substring(0, 3)} ${digits.substring(3)}`;
    }
    return digits;
};

/**
 * Format phone number for input (as user types)
 */
export const formatPhoneInput = (value) => {
    // Remove all non-digit characters
    let digits = value.replace(/\D/g, '');

    // Handle numbers starting with 0 (remove the leading 0)
    if (digits.startsWith('0')) {
        digits = digits.substring(1);
    }

    // Handle numbers starting with 61 (remove country code)
    if (digits.startsWith('61')) {
        digits = digits.substring(2);
    }

    // Limit to 9 digits
    digits = digits.substring(0, 9);

    // Format with spaces
    if (digits.length >= 7) {
        return `${digits.substring(0, 3)} ${digits.substring(3, 6)} ${digits.substring(6)}`;
    } else if (digits.length >= 4) {
        return `${digits.substring(0, 3)} ${digits.substring(3)}`;
    }
    return digits;
};

/**
 * Get all designers for a company
 */
export const getDesigners = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email')
            .eq('company_id', companyId)
            .eq('role', 'designer')
            .eq('status', 'active')
            .order('username');

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error fetching designers:', error);
        throw error;
    }
};

/**
 * Get all estimators for a company
 */
export const getEstimators = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email')
            .eq('company_id', companyId)
            .eq('role', 'estimator')
            .eq('status', 'active')
            .order('username');

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error fetching estimators:', error);
        throw error;
    }
};

/**
 * Get all production users for a company
 */
export const getProductionUsers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email, role')
            .eq('company_id', companyId)
            .eq('status', 'active')
            .in('role', ['admin', 'super_admin', 'designer']) // Users who can handle production
            .order('username');

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error fetching production users:', error);
        throw error;
    }
};

/**
 * Get a single user by ID (including all fields and company info)
 */
export const getUserById = async (userId) => {
    try {
        const { data: user, error } = await supabase
            .from('users')
            .select(`
                *,
                companies (
                    id,
                    name,
                    slug,
                    logo_url,
                    primary_color,
                    secondary_color
                )
            `)
            .eq('id', userId)
            .single();

        if (error) throw error;

        return user;
    } catch (error) {
        console.error('Error fetching user:', error);
        throw error;
    }
};

/**
 * Update an existing user
 */
export const updateUser = async (userId, userData) => {
    try {
        const { data: user, error } = await supabase
            .from('users')
            .update({
                username: userData.username,
                email: userData.email,
                full_name: userData.full_name || null,
                phone: userData.phone || null,
                avatar_url: userData.avatar_url || null,

                // Employment Info
                role: userData.role,
                position: userData.position || null,
                status: userData.status,

                // Emergency Contact
                emergency_contact_name: userData.emergency_contact_name || null,
                emergency_contact_phone: userData.emergency_contact_phone || null,

                // Vehicle Info
                license_number: userData.license_number || null,
                license_expiry: userData.license_expiry || null,
                vehicle_type: userData.vehicle_type || null,
                vehicle_registration: userData.vehicle_registration || null,

                // Preferences
                notification_preferences: userData.notification_preferences || {},
                theme_preference: userData.theme_preference || 'system',
                language: userData.language || 'en',

                updated_at: new Date().toISOString()
            })
            .eq('id', userId)
            .select()
            .single();

        if (error) throw error;

        return { user, message: 'User updated successfully' };
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
};